import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/** URL param holding the event shown in the event detail panel (`?event=<id>`). */
export const SELECTED_EVENT_PARAM = "event";

/**
 * The event open in the detail panel, kept in the URL so it survives a refresh and
 * can be linked to (e.g. the "Back" link of the contamination declaration editor).
 * Every other query param (filters, search, pagination) is left untouched.
 *
 * Pass `{ replace: true }` while paging through events so the history doesn't fill up.
 */
export const useSelectedEventParam = (): [
    string | null,
    (id: string | number | null, options?: { replace?: boolean }) => void,
] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedEventId = searchParams.get(SELECTED_EVENT_PARAM);

    const setSelectedEventId = useCallback(
        (id: string | number | null, options?: { replace?: boolean }) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (id === null) next.delete(SELECTED_EVENT_PARAM);
                    else next.set(SELECTED_EVENT_PARAM, String(id));
                    return next;
                },
                { replace: options?.replace }
            );
        },
        [setSearchParams]
    );

    return [selectedEventId, setSelectedEventId];
};
